/**
 * Recursive step renderer reused at the pipeline root and inside every loop
 * body. The DropStrips and the Add Step dropdown both target the container
 * identified by `parentLoopId` — `null` for root, a loop's id for its body.
 *
 * `scope` is the cumulative list of loop variables in scope from enclosing
 * loops; an inner loop appends its own variable for its own body's scope.
 * Rendered as a chip strip when non-empty, purely informational.
 */
import React, { useCallback, useState } from "react"
import { Button, Dropdown } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faPlus } from "@fortawesome/free-solid-svg-icons"
import {
  StepIssue,
  TransformStep,
  TransformStepType,
  useQueryBuilderStore,
} from "stores/queryBuilderStore"
import DropStrip from "./DropStrip.component"
import StepMenu from "./StepMenu.component"
import StepRow from "./StepRow.component"

interface StepListProps {
  steps: TransformStep[]
  parentLoopId: string | null
  scope: string[]
  compact: boolean
  expandedIds: Set<string>
  toggleExpanded: (id: string) => void
  /** Map of stepId → issues for the whole pipeline. Each StepList passes it
   *  unchanged to its StepRows so nested loops surface their body steps'
   *  issues without each layer recomputing. */
  allIssues: Map<string, StepIssue[]>
}

const StepList: React.FC<StepListProps> = ({
  steps,
  parentLoopId,
  scope,
  compact,
  expandedIds,
  toggleExpanded,
  allIssues,
}) => {
  const addStep = useQueryBuilderStore((s) => s.addTransformStep)
  const [menuOpen, setMenuOpen] = useState(false)
  const handlePick = useCallback(
    (type: TransformStepType) => {
      addStep(type, { parentLoopId })
      setMenuOpen(false)
    },
    [addStep, parentLoopId]
  )

  const isInsideLoop = parentLoopId !== null
  const addLabel = isInsideLoop
    ? steps.length === 0
      ? "Add step inside loop"
      : "Add another step"
    : "Add another step"

  return (
    <div
      className={
        isInsideLoop
          ? "qc-loop__body qc-transform__timeline"
          : "qc-transform__timeline"
      }
    >
      {scope.length > 0 && (
        <div className="qc-loop__scope" aria-label="Loop variables in scope">
          <span className="qc-loop__scope-label">Loop variables:</span>
          {scope.map((v) => (
            <span key={v} className="qc-mono qc-loop__scope-chip">
              {v}
            </span>
          ))}
        </div>
      )}
      {steps.map((step, idx) => (
        <React.Fragment key={step.id}>
          <DropStrip index={idx} parentLoopId={parentLoopId} />
          <StepRow
            step={step}
            index={idx}
            compact={compact}
            expanded={expandedIds.has(step.id)}
            onToggleExpanded={() => toggleExpanded(step.id)}
            scope={scope}
            expandedIds={expandedIds}
            toggleExpanded={toggleExpanded}
            issues={allIssues.get(step.id)}
            allIssues={allIssues}
          />
        </React.Fragment>
      ))}
      <DropStrip index={steps.length} parentLoopId={parentLoopId} />
      <div className="qc-transform__add-row">
        <span className="qc-step-row__rail" aria-hidden="true">
          <span className="qc-step-row__marker qc-step-row__marker--add">
            <FontAwesomeIcon icon={faPlus} />
          </span>
        </span>
        <Dropdown
          open={menuOpen}
          onOpenChange={setMenuOpen}
          trigger={["click"]}
          overlayClassName="qc-step-menu-overlay"
          placement="bottomLeft"
          getPopupContainer={(trigger) =>
            (trigger.closest(".qc-transform-scroll") as HTMLElement) ||
            document.body
          }
          popupRender={() => <StepMenu onPick={handlePick} />}
        >
          <Button
            type="dashed"
            className="self-start"
            icon={<FontAwesomeIcon icon={faPlus} />}
          >
            {addLabel}
            <FontAwesomeIcon
              icon={faChevronDown}
              className="qc-step__caret"
            />
          </Button>
        </Dropdown>
      </div>
      {isInsideLoop && <div className="qc-loop__endfor">ENDFOR</div>}
    </div>
  )
}

export default StepList
